import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, mergeMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FirebaseService } from './firebase.service';
import { ErrorService } from './error.service';

export type Product = {
  id: string;
  active: boolean;
  code: string;
  variance_code: string;
  title: string;
  description: string;
  standalone: boolean;
  is_default: boolean;
  type: ProductType;
  is_service: boolean;
  expiration_type: ExpirationType;
  requires_parent: boolean;
  quickbooks_category_id: number;
  created_at: string;
  updated_at: string;
  active_desktop_license_count?: number;
};

export type ProductType =
  | 'api'
  | 'cass'
  | 'desktop'
  | 'physical'
  | 'service'
  | 'sorter'
  | 'toolkit'
  | 'tracking';

export type ExpirationType =
  | 'desktop'
  | 'annual'
  | 'force_extension'
  | 'physical'
  | 'transactional';

export const ExpirationTypeDetails: {
  [Property in ExpirationType]: { displayName: string; description: string };
} = {
  desktop: {
    displayName: 'Normal desktop product expiration',
    description: 'Uses the DVD release cycle for expiration date calculations.',
  },
  annual: {
    displayName: 'Annual expiration',
    description:
      'Annual expiration starts from the date the product is purchased.',
  },
  force_extension: {
    displayName: 'Force extension expiration',
    description:
      'Force extension expiration will extend the expiration date ' +
      'of other products associated with this product. Used for products ' +
      'that will have a higher initial subscription price and then lower for ' +
      'renewals.',
  },
  physical: {
    displayName: 'Physical product purchases',
    description: 'Physical product purchases. Does not have an expiration.',
  },
  transactional: {
    displayName: 'Transactional product purchases',
    description:
      'Transactional product purchases. Does not have an expiration.',
  },
};

export type ProductVariant = {
  id: string;
  product_id: string;
  franchise_id?: string;
  description: string;
  active: boolean;
  is_default: boolean;
  delivery_method: string;
  duration_months: number;
  display_duration: string;
  price_lock_level: number;
  price_key: string;
  site_price_key: string;
  renewal_price_key: string;
  site_renewal_price_key: string;
  tax_code: string;
  site_tax_code: string;
  renewal_tax_code: string;
  site_renewal_tax_code: string;
  price: number;
  site_price: number;
  renewal_price: number;
  site_renewal_price: number;
  created_at: string;
  updated_at: string;
  active_desktop_license_count?: number;
};

export type ProductWithVariants = {
  product: Product;
  variants: ProductVariant[];
  active_desktop_license_count?: number;
};

export type ProductResponse = {
  product: Product;
};

export type ProductsResponse = {
  products: Product[];
};

export type ProductDetailsResponse = {
  product_with_variants: ProductWithVariants;
};

export type ProductVariantResponse = {
  product_variant: ProductVariant;
};

export type ProductVariantsResponse = {
  product_variants: ProductVariant[];
};

export type NewProductRequest = {
  active: boolean;
  code: string;
  variance_code: string;
  title: string;
  description: string;
  standalone: boolean;
  type: ProductType;
  is_service: boolean;
  expiration_type: ExpirationType;
  requires_parent: boolean;
  quickbooks_category_id: number;
};

export type UpdateProductRequest = {
  active?: boolean;
  code?: string;
  variance_code?: string;
  title?: string;
  description?: string;
  standalone?: boolean;
  is_default?: boolean;
  type?: ProductType;
  is_service?: boolean;
  expiration_type?: ExpirationType;
  requires_parent?: boolean;
  quickbooks_category_id?: number;
};

export type NewProductVariantRequest = {
  product_id: string;
  franchise_id?: string;
  description: string;
  active: boolean;
  is_default: boolean;
  delivery_method: string;
  duration_months: number;
  price_lock_level: number;
  price_key: string;
  site_price_key?: string;
  renewal_price_key?: string;
  site_renewal_price_key?: string;
  tax_code: string;
  site_tax_code?: string;
  renewal_tax_code?: string;
  site_renewal_tax_code?: string;
  price: number;
  site_price?: number;
  renewal_price?: number;
  site_renewal_price?: number;
};

export type UpdateProductVariantRequest = {
  product_id?: string;
  franchise_id?: string;
  description?: string;
  active?: boolean;
  is_default?: boolean;
  delivery_method?: string;
  duration_months?: number;
  price_lock_level?: number;
  price_key?: string;
  site_price_key?: string;
  renewal_price_key?: string;
  site_renewal_price_key?: string;
  tax_code?: string;
  site_tax_code?: string;
  renewal_tax_code?: string;
  site_renewal_tax_code?: string;
  price?: number;
  site_price?: number;
  renewal_price?: number;
  site_renewal_price?: number;
};

export type ProductPricingGroup = {
  product_id: string;
  code: string;
  variance_code: string;
  title: string;
  product_type: string;
  requires_parent: boolean;
  price_12_month?: number | null;
  price_24_month?: number | null;
  price_36_month?: number | null;
  site_price_12_month?: number | null;
  site_price_24_month?: number | null;
  site_price_36_month?: number | null;
};

export type ProductPricingResponse = {
  groups: ProductPricingGroup[];
};

export type SquashVariantSummary = {
  id: string;
  duration_months: number;
  delivery_method: string;
  price_lock_level?: number;
  price?: number | null;
  site_price?: number | null;
  renewal_price?: number | null;
  site_renewal_price?: number | null;
};

export type VariantMigrationMapping = {
  from_variant_id: string;
  to_variant_id: string;
  from_product_id: string;
  to_product_id: string;
  from_product_code: string;
  from_product_title: string;
  to_product_code: string;
  to_product_title: string;
  from_variant: SquashVariantSummary;
  to_variant: SquashVariantSummary;
  license_count: number;
  invoice_relation_count: number;
};

export type SquashCloseMatch = {
  primary_variant: SquashVariantSummary;
  partial_price_match: boolean;
  close_price_match: boolean;
  matched_price_fields?: string[];
  close_price_fields?: string[];
  mismatched_price_fields?: string[];
};

export type UnmatchedChildVariant = {
  variant_id: string;
  product_id: string;
  product_code: string;
  product_title: string;
  variant: SquashVariantSummary;
  reason: string;
  active_license_count: number;
  conflicting_variant_ids?: string[];
  close_matches?: SquashCloseMatch[];
};

export type ProductSquashPreviewResponse = {
  primary_product_id: string;
  child_product_ids: string[];
  variant_migrations: VariantMigrationMapping[];
  unmatched_child_variants: UnmatchedChildVariant[];
  matched_count: number;
  unmatched_count: number;
};

export type ProductSquashSiblingsResponse = {
  products: Product[];
};

export type ProductSquashPreviewRequest = {
  primary_product_id: string;
  child_product_ids: string[];
};

export type SquashPrimaryGroup = {
  primary_product: Product;
  child_product_ids: string[];
  preview?: ProductSquashPreviewResponse | null;
};

export type ProductSquashSession = {
  groups: SquashPrimaryGroup[];
  active_primary_id: string | null;
  hide_already_selected?: boolean;
  preview_schema_version?: number;
};

export type ProductSquashExportGroup = {
  primary_product_id: string;
  child_product_ids: string[];
  variant_migrations: VariantMigrationMapping[];
  unmatched_child_variants: UnmatchedChildVariant[];
};

export type ProductSquashExport = {
  version: number;
  groups: ProductSquashExportGroup[];
  exported_at: string;
};

export type QuickbooksCategory = {
  id: number;
  class_name: string;
  parent_item_id?: string;
  class_id?: string;
  income_parent_account_name: string;
  income_parent_account_number: string;
  income_account_name: string;
  income_account_number: string;
  income_account_id?: string;
  created_at: string;
};

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  getAllProductsUrl = environment.serverUrl + '/products/get-all';
  getProductUrl = environment.serverUrl + '/products/get';
  productDetailsUrl = environment.serverUrl + '/products/get-details';
  createProductUrl = environment.serverUrl + '/products/create';
  getProductVariantUrl = environment.serverUrl + '/products/get-variant';
  createProductVariantUrl = environment.serverUrl + '/products/create-variant';
  durationOptionsUrl =
    environment.serverUrl + '/products/get-variant-durations';
  updateProductUrl = environment.serverUrl + '/products/update';
  updateProductVariantUrl = environment.serverUrl + '/products/update-variant';
  getQuickbooksCategoriesUrl =
    environment.serverUrl + '/quickbooks/all-categories';
  productPricingUrl = environment.serverUrl + '/products/pricing';
  squashSiblingsUrl = environment.serverUrl + '/products/squash/siblings';
  squashPreviewUrl = environment.serverUrl + '/products/squash/preview';

  productTypes: ProductType[] = [
    'api',
    'cass',
    'desktop',
    'physical',
    'service',
    'sorter',
    'toolkit',
    'tracking',
  ];

  deliveryMethods = ['DNL', 'XXX'];

  constructor(
    private http: HttpClient,
    private errorService: ErrorService,
    private firebaseService: FirebaseService
  ) {}

  getAllProducts() {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<ProductsResponse>(this.getAllProductsUrl, {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        });
      })
    );
  }

  getProductPricing(priceLockLevel: number) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<ProductPricingResponse>(this.productPricingUrl, {
          headers: {
            Authorization: 'Bearer ' + token,
          },
          params: {
            price_lock_level: priceLockLevel,
          },
        });
      })
    );
  }

  getProduct(productId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<ProductResponse>(
          this.getProductUrl + '/' + productId,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  getProductDetails(productId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<ProductDetailsResponse>(
          this.productDetailsUrl + '/' + productId,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  createProduct(product: NewProductRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<ProductResponse>(this.createProductUrl, product, {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        });
      })
    );
  }

  getProductVariant(productVariantId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<ProductVariantResponse>(
          this.getProductVariantUrl + '/' + productVariantId,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  createProductVariant(productVariant: NewProductVariantRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<ProductVariantResponse>(
          this.createProductVariantUrl,
          productVariant,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  getDurationOptions(productVariant: ProductVariant) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<ProductVariantsResponse>(
          this.durationOptionsUrl + '/' + productVariant.id,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  updateProduct(productId: string, productUpdate: UpdateProductRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<ProductResponse>(
          this.updateProductUrl + '/' + productId,
          productUpdate,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  updateProductVariant(
    productVariantId: string,
    productVariantUpdate: UpdateProductVariantRequest
  ) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<ProductVariantResponse>(
          this.updateProductVariantUrl + '/' + productVariantId,
          productVariantUpdate,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  getSquashSiblings(code: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<ProductSquashSiblingsResponse>(
          `${this.squashSiblingsUrl}/${encodeURIComponent(code)}`,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  previewSquash(request: ProductSquashPreviewRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<ProductSquashPreviewResponse>(
          this.squashPreviewUrl,
          request,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  getQuickbooksCategories() {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<QuickbooksCategory[]>(
          this.getQuickbooksCategoriesUrl,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      }),
      catchError(
        this.errorService.handleObservableError<QuickbooksCategory[]>()
      )
    );
  }
}
